import { useMutation, useQuery } from 'react-query';
import { HubspotService, HubspotUserData, upsertContactProperties } from 'services/hubspot';
import { UsersService, UserInfo } from 'services/users';

export type Contact = HubspotUserData;

export const emptyContact: Contact = {
  id: '',
  hubspotId: '',
  email: '',
};

export const getContactById = async (contactId: string) => {
  if (!contactId) {
    return emptyContact;
  }
  try {
    const hubspotUserProp = await HubspotService.getHubSpotUserProperties(
      contactId,
    );
    return hubspotUserProp;
  } catch (hubspotErr) {
    /**
     * Not throwing error because we need next call to work smoothly and
     *  hubspot call doesn't have
     * any dependency on next call
     */
    // eslint-disable-next-line no-console
    console.error(hubspotErr);
    return emptyContact;
  }
};

export const updateContact = async (
  contactId: string,
  data: Record<string, any>,
) => {
  const result = await HubspotService.updateContact(contactId, data);
  return {
    ...data,
    ...result.data,
  };
};

/**
 * This attempts to upsert a contact record based on the email
 * address of the user and then link it to the user record.
 *
 * Makes an extra HTTP request that I'm not sure is neccessary, and can
 * be optimized if needed
 *
 * @param user UserInfo
 * @returns HubspotUserData
 */
export const upsertContact = async (user: UserInfo) => {
  const { contactId } = await upsertContactProperties({
    email: user.email,
  });

  await UsersService.updateUserById(user.id, { contactId });

  return contactId;
};

export const useGetContact = (contactId: string) =>
  useQuery<Contact, unknown>(
    ['contact', contactId],
    () => getContactById(contactId),
    {
      placeholderData: emptyContact,
    },
  );

const contact = {
  UpsertContact: (queryProps?: any) =>
    useMutation(({ user }: any) => upsertContact(user), queryProps),
  GetContact: (id: string) =>
    useQuery<Contact, unknown>(['contact', id], () => getContactById(id), {
      placeholderData: emptyContact,
    }),
  UpdateContact: (queryProps?: any) =>
    useMutation(({ id, data }: any) => updateContact(id, data), queryProps),
};

export default contact;
